// src/pages/MyChatbots.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Paper,
    Divider,
    Tooltip,
    Chip,
    ListItemButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Menu,
    MenuItem,
    Alert,
    Snackbar,
    CircularProgress,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axiosInstance from '../utils/axiosInstance';
import { useChatbots } from '../contexts/ChatbotContext';

export default function MyChatbots() {
    const { chatbots, activeChatbot, switchActiveChatbot, refreshChatbots, isLoading } = useChatbots();
    const navigate = useNavigate();

    // States for pending process chatbot
    const [pendingChatbot, setPendingChatbot] = useState(null);
    const [loadingPending, setLoadingPending] = useState(true);

    // States for action menus and dialogs
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedBot, setSelectedBot] = useState(null);
    
    // Delete dialog states
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    
    // Convert type dialog states
    const [convertDialogOpen, setConvertDialogOpen] = useState(false);
    const [converting, setConverting] = useState(false);
    
    // Snackbar states
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Fetch pending process chatbot on mount
    useEffect(() => {
        fetchPendingChatbot();
    }, []);

    const fetchPendingChatbot = async () => {
        try {
            setLoadingPending(true);
            const response = await axiosInstance.get('/chatbots/user/pending-process-bot');
            if (response.data.success && response.data.data.chatbot) {
                setPendingChatbot(response.data.data.chatbot);
            } else {
                setPendingChatbot(null);
            }
        } catch (error) {
            console.error('Error fetching pending chatbot:', error);
            setPendingChatbot(null);
        } finally {
            setLoadingPending(false);
        }
    };

    const handleSetDefault = async (chatbotId) => {
        try {
            await axiosInstance.put(`/chatbots/${chatbotId}/set-default`);
            setSnackbar({ open: true, message: 'Default chatbot updated!', severity: 'success' });
            refreshChatbots();
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to update default chatbot.', severity: 'error' });
            console.error(error);
        }
    };

    // Menu handlers
    const handleMenuOpen = (event, bot) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedBot(bot);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    // Delete handlers
    const handleDeleteClick = () => {
        handleMenuClose();
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedBot) return;
        
        setDeleting(true);
        try {
            await axiosInstance.delete(`/chatbots/${selectedBot.id}`);
            setSnackbar({ open: true, message: 'Chatbot deleted successfully!', severity: 'success' });
            setDeleteDialogOpen(false);
            setSelectedBot(null);
            refreshChatbots();
            fetchPendingChatbot(); // Refresh pending chatbot as well
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to delete chatbot.';
            setSnackbar({ open: true, message, severity: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setSelectedBot(null);
    };

    // Convert type handlers
    const handleConvertClick = () => {
        handleMenuClose();
        setConvertDialogOpen(true);
    };

    const handleConvertConfirm = async () => {
        if (!selectedBot) return;
        
        const newType = selectedBot.chatbot_type === 'personal' ? 'business' : 'personal';
        
        setConverting(true);
        try {
            await axiosInstance.put(`/chatbots/${selectedBot.id}/convert-type`, {
                chatbot_type: newType
            });
            setSnackbar({ 
                open: true, 
                message: `Chatbot converted to ${newType} successfully!`, 
                severity: 'success' 
            });
            setConvertDialogOpen(false);
            setSelectedBot(null);
            refreshChatbots();
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to convert chatbot type.';
            setSnackbar({ open: true, message, severity: 'error' });
        } finally {
            setConverting(false);
        }
    };

    const handleConvertCancel = () => {
        setConvertDialogOpen(false);
        setSelectedBot(null);
    };

    // Navigation handlers
    const handleContinueSetup = (chatbotIdentity = null) => {
        if (chatbotIdentity) {
            navigate(`/chatbot/${chatbotIdentity}/create`);
        } else {
            navigate(`/chatbot/create`);
        }
    };

    const handleCreateNewChatbot = () => {
        navigate(`/chatbot/create`);
    };

    const handleOpenChatbot = (bot) => {
        switchActiveChatbot(bot.id, { navigateToDashboard: true });
    };

    // Helper to get chatbot type chip
    const getChatbotTypeChip = (type) => {
        const isPersonal = type === 'personal';
        return (
            <Chip
                icon={isPersonal ? <PersonIcon fontSize="small" /> : <BusinessIcon fontSize="small" />}
                label={isPersonal ? 'Personal' : 'Business'}
                size="small"
                color={isPersonal ? 'default' : 'primary'}
                variant="outlined"
                sx={{ fontSize: '0.75rem', height: '24px' }}
            />
        );
    };

    // Helper to get status chip
    const getStatusChip = (bot) => {
        if (!bot.is_process_completed) {
            return (
                <Chip
                    label="Setup Incomplete"
                    size="small"
                    color="warning"
                    sx={{ fontSize: '0.75rem', height: '24px', ml: 1 }}
                />
            );
        }
        return null;
    };

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Determine main action button
    const renderMainActionButton = () => {
        if (loadingPending) {
            return (
                <Button variant="contained" disabled startIcon={<CircularProgress size={20} />}>
                    Loading...
                </Button>
            );
        }

        if (pendingChatbot) {
            return (
                <Button
                    variant="contained"
                    color="warning"
                    startIcon={<PlayArrowIcon />}
                    onClick={handleCreateNewChatbot}
                >
                    Continue Setup: {pendingChatbot.chatbot_name}
                </Button>
            );
        }

        return (
            <Button
                variant="contained"
                startIcon={<AddCircleIcon />}
                onClick={handleCreateNewChatbot}
            >
                Create New Chatbot
            </Button>
        );
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 5, mt: 10 }}>
                <CircularProgress size={30} />
                <Typography ml={2}>Loading chatbots...</Typography>
            </Box>
        );
    }

    return (
        <>
            <Paper sx={{ p: 3, mt: 10, maxWidth: '900px', mx: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h4" component="h1">
                        My Chatbots
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {renderMainActionButton()}
                    </Box>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {/* Pending Setup Alert */}
                {pendingChatbot && (
                    <Alert 
                        severity="warning" 
                        sx={{ mb: 3 }}
                        icon={<WarningAmberIcon />}
                    >
                        <Typography variant="body2">
                            <strong>"{pendingChatbot.chatbot_name}"</strong> setup is incomplete. 
                            Current step: {pendingChatbot.current_process_step || 'Getting started'}
                        </Typography>
                    </Alert>
                )}

                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Manage your chatbots here. Click on any chatbot to open it. 
                    The default chatbot (marked with <StarIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />) 
                    will be selected automatically when you log in.
                </Typography>

                {chatbots.length === 0 ? (
                    <Box textAlign="center" py={5}>
                        <SmartToyIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                            No Chatbots Yet
                        </Typography>
                        <Typography color="text.secondary" sx={{ mb: 3 }}>
                            Create your first chatbot to get started
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddCircleIcon />}
                            onClick={handleCreateNewChatbot}
                        >
                            Create Your First Chatbot
                        </Button>
                    </Box>
                ) : (
                    <List>
                        {chatbots.map((bot) => (
                            <ListItem
                                key={bot.id}
                                secondaryAction={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {/* Set Default Button */}
                                        <Tooltip title={bot.is_default ? "This is your default chatbot" : "Make default"}>
                                            <span>
                                                <IconButton
                                                    edge="end"
                                                    aria-label="set-default"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSetDefault(bot.id);
                                                    }}
                                                    disabled={bot.is_default}
                                                >
                                                    {bot.is_default ? <StarIcon color="primary" /> : <StarBorderIcon />}
                                                </IconButton>
                                            </span>
                                        </Tooltip>

                                        {/* Open Button */}
                                        <Tooltip title="Open chatbot">
                                            <IconButton
                                                edge="end"
                                                aria-label="open"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpenChatbot(bot);
                                                }}
                                            >
                                                <OpenInNewIcon />
                                            </IconButton>
                                        </Tooltip>

                                        {/* More Actions Menu */}
                                        <Tooltip title="More actions">
                                            <IconButton
                                                edge="end"
                                                aria-label="more-actions"
                                                onClick={(e) => handleMenuOpen(e, bot)}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                }
                                sx={{
                                    border: bot.id === activeChatbot?.id ? '2px solid' : '1px solid',
                                    borderColor: bot.id === activeChatbot?.id ? 'primary.main' : 'divider',
                                    borderRadius: 2,
                                    mb: 1.5,
                                    bgcolor: 'background.paper',
                                    '&:hover': {
                                        bgcolor: 'action.hover',
                                    }
                                }}
                                disablePadding
                            >
                                <ListItemButton onClick={() => handleOpenChatbot(bot)}>
                                    <ListItemIcon>
                                        <SmartToyIcon color={bot.id === activeChatbot?.id ? 'primary' : 'action'} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Typography variant="subtitle1" fontWeight={600}>
                                                    {bot.chatbot_name}
                                                </Typography>
                                                {getChatbotTypeChip(bot.chatbot_type)}
                                                {getStatusChip(bot)}
                                                {bot.id === activeChatbot?.id && (
                                                    <Chip
                                                        label="Active"
                                                        size="small"
                                                        color="success"
                                                        sx={{ fontSize: '0.7rem', height: '20px' }}
                                                    />
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            <Typography variant="body2" color="text.secondary">
                                                ID: {bot.chatbot_identity?.slice(0, 8)}... • 
                                                Created: {new Date(bot.created_at).toLocaleDateString()}
                                            </Typography>
                                        }
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Paper>

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {/* Continue Setup - only show if incomplete */}
                {selectedBot && !selectedBot.is_process_completed && (
                    <MenuItem onClick={() => {
                        handleMenuClose();
                        handleCreateNewChatbot();
                    }}>
                        <ListItemIcon>
                            <PlayArrowIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Continue Setup</ListItemText>
                    </MenuItem>
                )}

                {/* Convert Type */}
                <MenuItem onClick={handleConvertClick}>
                    <ListItemIcon>
                        <SwapHorizIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                        Convert to {selectedBot?.chatbot_type === 'personal' ? 'Business' : 'Personal'}
                    </ListItemText>
                </MenuItem>

                <Divider />

                {/* Delete - disabled for default chatbot */}
                <MenuItem 
                    onClick={handleDeleteClick} 
                    disabled={selectedBot?.is_default}
                    sx={{ color: selectedBot?.is_default ? 'text.disabled' : 'error.main' }}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" color={selectedBot?.is_default ? 'disabled' : 'error'} />
                    </ListItemIcon>
                    <ListItemText>
                        {selectedBot?.is_default ? 'Cannot delete default' : 'Delete Chatbot'}
                    </ListItemText>
                </MenuItem>
            </Menu>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DeleteIcon color="error" />
                    Delete Chatbot
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete <strong>"{selectedBot?.chatbot_name}"</strong>?
                        <br /><br />
                        This action cannot be undone. All associated data, configurations, 
                        and chat history will be permanently removed.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm} 
                        color="error" 
                        variant="contained"
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Convert Type Confirmation Dialog */}
            <Dialog
                open={convertDialogOpen}
                onClose={handleConvertCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SwapHorizIcon color="primary" />
                    Convert Chatbot Type
                </DialogTitle>
                <DialogContent>
                    <DialogContentText component="div">
                        <Box sx={{ mb: 2 }}>
                            Convert <strong>"{selectedBot?.chatbot_name}"</strong> from{' '}
                            <Chip 
                                label={selectedBot?.chatbot_type} 
                                size="small" 
                                sx={{ mx: 0.5 }}
                            /> 
                            to{' '}
                            <Chip 
                                label={selectedBot?.chatbot_type === 'personal' ? 'business' : 'personal'} 
                                size="small" 
                                color="primary"
                                sx={{ mx: 0.5 }}
                            />?
                        </Box>

                        {/* Warning Alert */}
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Important: Additional Steps Required
                            </Typography>
                            <Typography variant="body2">
                                After converting the chatbot type, you will need to update the 
                                <strong> system prompt</strong> in the Chatbot Settings tab to match 
                                the new type's requirements.
                            </Typography>
                        </Alert>

                        {/* Info about differences */}
                        <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Type Differences:
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                <li>
                                    <Typography variant="body2">
                                        <strong>Business:</strong> Visible to all team members, 
                                        suitable for customer-facing interactions
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2">
                                        <strong>Personal:</strong> Only visible to admins, 
                                        suitable for private or testing purposes
                                    </Typography>
                                </li>
                            </Box>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            Do you want to proceed with the conversion?
                        </Typography>
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleConvertCancel} disabled={converting}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConvertConfirm} 
                        color="primary" 
                        variant="contained"
                        disabled={converting}
                        startIcon={converting ? <CircularProgress size={20} /> : <SwapHorizIcon />}
                    >
                        {converting ? 'Converting...' : 'Yes, Convert Type'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleSnackbarClose} 
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}